import { useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import { dataService } from '../../services/DataService'
import { getOrderQuantityErrorKey, validateOrderQuantity } from '../../js/utils/OrderQuantityRules'

const IMPORT_COLUMNS = [
  'itemNumber',
  'requestedQty',
  'productType',
  'outOfCatalogProduct',
  'salesPrice',
  'discountPercent',
  'netSalesPrice',
  'lineAmount',
  'displayOrder',
  'grouping',
  'itemReferenceEasyVent',
  'externalItemNumber',
  'salesUnitSymbol'
]

const IMPORT_COLUMN_ALIASES = {
  itemnumber: 'itemNumber',
  item: 'itemNumber',
  mserpitemnumber: 'itemNumber',
  requestedqty: 'requestedQty',
  requestedquantity: 'requestedQty',
  requestedsalesquantity: 'requestedQty',
  mserprequestedsalesquantity: 'requestedQty',
  producttype: 'productType',
  mserpproducttype: 'productType',
  mserpalgproducttype: 'productType',
  outofcatalogproduct: 'outOfCatalogProduct',
  mserpalgoutofcatalogproduct: 'outOfCatalogProduct',
  salesprice: 'salesPrice',
  mserpsalesprice: 'salesPrice',
  discountpercent: 'discountPercent',
  linediscountpercentage: 'discountPercent',
  mserplinediscountpercentage: 'discountPercent',
  netsalesprice: 'netSalesPrice',
  mserpalgnetsalesprice: 'netSalesPrice',
  lineamount: 'lineAmount',
  mserplineamount: 'lineAmount',
  displayorder: 'displayOrder',
  ord: 'displayOrder',
  reportdisplayorder: 'displayOrder',
  mserpalgreportdisplayorder: 'displayOrder',
  grouping: 'grouping',
  mserpalggrouping: 'grouping',
  itemreferenceeasyvent: 'itemReferenceEasyVent',
  mserpalgitemreferenceeasyvent: 'itemReferenceEasyVent',
  externalitemnumber: 'externalItemNumber',
  mserpexternalitemnumber: 'externalItemNumber',
  salesunitsymbol: 'salesUnitSymbol',
  mserpsalesunitsymbol: 'salesUnitSymbol'
}
const normalizeImportHeader = (header) => String(header || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '')

const parseImportNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const text = String(value ?? '').trim().replace(/\s/g, '')
  if (!text) return null

  const normalized = text.includes(',') && text.includes('.')
    ? (text.lastIndexOf(',') > text.lastIndexOf('.')
      ? text.replace(/\./g, '').replace(',', '.')
      : text.replace(/,/g, ''))
    : text.replace(',', '.')
  const number = Number(normalized)
  return Number.isFinite(number) ? number : null
}

const normalizeProductType = (value) => {
  const normalized = String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
  if (!normalized || normalized === '200000000' || normalized === 'existing' || normalized === 'existente' || normalized === 'existant') return '200000000'
  if (normalized === '200000001' || normalized === 'outofcatalog' || normalized === 'out of catalog' || normalized === 'fuera de catalogo' || normalized === 'hors catalogue') return '200000001'
  return null
}

export const useQuotationLinesImport = ({
  t,
  quotationNumber,
  quotationDetails,
  lines,
  editedLines,
  isEditMode,
  smmParameters,
  setEditedLines,
  setModifiedFields,
  setIsEditMode,
  calculateDefaultNetSalesPrice,
  calculateVariationPercentage,
  calculateLineAmount
}) => {
  const [importErrors, setImportErrors] = useState([])
  const [importSummary, setImportSummary] = useState(null)
  const importInputRef = useRef(null)

  const getImportColumnLabel = (column) => {
    const labels = {
      itemNumber: t('quotation:lines.columns.itemNumber'),
      requestedQty: t('quotation:lines.columns.requestedQty'),
      productType: t('quotation:lines.columns.productType'),
      outOfCatalogProduct: t('quotation:lines.columns.outOfCatalog'),
      salesPrice: t('quotation:lines.columns.salesPrice'),
      discountPercent: t('quotation:lines.columns.discountPercent'),
      netSalesPrice: t('quotation:lines.columns.netSalesPrice'),
      lineAmount: t('quotation:lines.columns.lineAmount'),
      displayOrder: t('quotation:lines.columns.ord'),
      grouping: t('quotation:lines.columns.grouping'),
      itemReferenceEasyVent: t('quotation:lines.columns.itemRefEasyVent'),
      externalItemNumber: t('quotation:lines.columns.externalItemNumber'),
      salesUnitSymbol: t('quotation:lines.columns.salesUnitSymbol', { defaultValue: 'Sales unit' })
    }
    return labels[column] || column
  }

  const downloadImportTemplate = (bookType = 'xlsx') => {
    const templateRow = IMPORT_COLUMNS.reduce((row, column) => ({ ...row, [column]: '' }), {})
    const worksheet = XLSX.utils.json_to_sheet([templateRow], { header: IMPORT_COLUMNS })
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Quotation lines')
    XLSX.writeFile(workbook, `quotation-lines-template.${bookType}`, { bookType })
  }

  const validateImportedRow = (row, rowNumber) => {
    const errors = []
    const addError = (field, message) => errors.push({ field, message })
    const itemNumber = String(row.itemNumber ?? '').trim()
    const outOfCatalogProduct = String(row.outOfCatalogProduct ?? '').trim()
    const productType = normalizeProductType(row.productType)
    const requestedQty = parseImportNumber(row.requestedQty)
    const salesPrice = parseImportNumber(row.salesPrice)
    const discountPercent = parseImportNumber(row.discountPercent)
    const netSalesPrice = parseImportNumber(row.netSalesPrice)
    const lineAmount = parseImportNumber(row.lineAmount)
    const displayOrder = parseImportNumber(row.displayOrder)

    if (!productType) addError('productType', t('quotation:lines.import.errors.invalidProductType'))
    if (productType === '200000000' && !itemNumber) addError('itemNumber', t('quotation:lines.import.errors.itemRequired'))
    if (productType === '200000001' && !outOfCatalogProduct) addError('outOfCatalogProduct', t('quotation:lines.import.errors.outOfCatalogRequired'))
    if (requestedQty === null || requestedQty <= 0) addError('requestedQty', t('quotation:lines.import.errors.quantityRequired'))

    const numericFields = [
      ['salesPrice', row.salesPrice, salesPrice],
      ['discountPercent', row.discountPercent, discountPercent],
      ['netSalesPrice', row.netSalesPrice, netSalesPrice],
      ['lineAmount', row.lineAmount, lineAmount],
      ['displayOrder', row.displayOrder, displayOrder]
    ]
    numericFields.forEach(([field, rawValue, parsedValue]) => {
      if (String(rawValue ?? '').trim() && parsedValue === null) addError(field, t('quotation:lines.import.errors.invalidNumber'))
    })
    if (salesPrice !== null && salesPrice < 0) addError('salesPrice', t('quotation:lines.import.errors.nonNegative'))
    if (discountPercent !== null && (discountPercent < 0 || discountPercent > 100)) addError('discountPercent', t('quotation:lines.import.errors.discountRange'))
    if (netSalesPrice !== null && netSalesPrice < 0) addError('netSalesPrice', t('quotation:lines.import.errors.nonNegative'))
    if (lineAmount !== null && lineAmount < 0) addError('lineAmount', t('quotation:lines.import.errors.nonNegative'))
    if (displayOrder !== null && (!Number.isInteger(displayOrder) || displayOrder < 0)) addError('displayOrder', t('quotation:lines.import.errors.orderInteger'))

    return {
      rowNumber,
      errors,
      values: { itemNumber, outOfCatalogProduct, productType, requestedQty, salesPrice, discountPercent, netSalesPrice, lineAmount, displayOrder }
    }
  }

  const buildImportedLine = (validatedRow, index, displayOrder) => {
    const { values } = validatedRow
    const actualQuotationNumber = quotationDetails?.mserp_salesquotationnumber || quotationNumber
    const salesPrice = values.salesPrice ?? 0
    const discountPercent = values.discountPercent ?? 0
    const calculatedNetSalesPrice = values.netSalesPrice ?? calculateDefaultNetSalesPrice(salesPrice, discountPercent)
    const productType = values.productType
    const lineAmount = values.lineAmount ?? calculateLineAmount(values.requestedQty, calculatedNetSalesPrice)
    const defaultNetSalesPrice = calculateDefaultNetSalesPrice(salesPrice, discountPercent)
    const outOfStockItemNumber = smmParameters?.mserp_alg_productoutofcatalog || 'L999OutOfStock'

    return {
      id: `temp_import_${Date.now()}_${index}`,
      mserp_salesquotationnumber: actualQuotationNumber,
      mserp_itemnumber: productType === '200000001' ? outOfStockItemNumber : values.itemNumber,
      mserp_requestedsalesquantity: values.requestedQty,
      mserp_salespricequantity: 1,
      mserp_salesprice: salesPrice,
      mserp_linediscountpercentage: discountPercent,
      mserp_alg_netsalesprice: calculatedNetSalesPrice,
      mserp_alg_defaultnetsalesprice: defaultNetSalesPrice,
      mserp_alg_variationpercentage: productType === '200000001' ? discountPercent : calculateVariationPercentage(defaultNetSalesPrice, calculatedNetSalesPrice),
      mserp_lineamount: lineAmount,
      mserp_alg_reportdisplayorder: values.displayOrder ?? displayOrder,
      mserp_alg_grouping: String(validatedRow.grouping ?? '').trim(),
      mserp_alg_producttype: productType,
      mserp_alg_outofcatalogproduct: values.outOfCatalogProduct,
      mserp_alg_itemreferenceeasyvent: String(validatedRow.itemReferenceEasyVent ?? '').trim(),
      mserp_externalitemnumber: String(validatedRow.externalItemNumber ?? '').trim(),
      mserp_salesunitsymbol: String(validatedRow.salesUnitSymbol ?? '').trim(),
      _orderQuantitySettings: values.orderQuantitySettings || null,
      _isNew: true,
      _importRow: validatedRow.rowNumber
    }
  }

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setImportErrors([])
    setImportSummary(null)

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '', raw: true })
      const headerIndex = rows.findIndex(row => row.some(value => String(value ?? '').trim() !== ''))
      if (headerIndex === -1) throw new Error(t('quotation:lines.import.errors.emptyFile'))

      const headers = rows[headerIndex].map(value => String(value ?? '').trim())
      const mappedHeaders = headers.map(header => IMPORT_COLUMN_ALIASES[normalizeImportHeader(header)])
      const unknownHeaders = headers.filter((header, index) => header && !mappedHeaders[index])
      const recognizedColumns = mappedHeaders.filter(Boolean)

      if (unknownHeaders.length > 0) {
        setImportErrors([{ rowNumber: headerIndex + 1, errors: [{ field: 'header', message: t('quotation:lines.import.errors.unknownColumns', { columns: unknownHeaders.join(', ') }) }] }])
        return
      }
      if (!recognizedColumns.includes('itemNumber') || !recognizedColumns.includes('requestedQty')) {
        setImportErrors([{ rowNumber: headerIndex + 1, errors: [{ field: 'header', message: t('quotation:lines.import.errors.requiredColumns') }] }])
        return
      }

      const dataRows = rows.slice(headerIndex + 1)
        .map((row, index) => ({ row, rowNumber: headerIndex + index + 2 }))
        .filter(({ row }) => row.some(value => String(value ?? '').trim() !== ''))
      if (dataRows.length === 0) {
        setImportErrors([{ rowNumber: headerIndex + 1, errors: [{ field: 'file', message: t('quotation:lines.import.errors.noRows') }] }])
        return
      }

      const parsedRows = dataRows.map(({ row, rowNumber }) => {
        const mappedRow = {}
        mappedHeaders.forEach((column, index) => {
          if (column && mappedRow[column] === undefined) mappedRow[column] = row[index]
        })
        return { ...validateImportedRow(mappedRow, rowNumber), ...mappedRow }
      })

      const productNumbers = [...new Set(
        parsedRows
          .filter(row => row.errors.length === 0 && row.values.productType === '200000000')
          .map(row => row.values.itemNumber)
          .filter(Boolean)
      )]
      const settingsResults = await Promise.all(productNumbers.map(async (itemNumber) => ({
        itemNumber,
        result: await dataService.getProductOrderSettings(itemNumber)
      })))
      const settingsByProduct = new Map(settingsResults.map(({ itemNumber, result }) => [itemNumber, result]))

      parsedRows
        .filter(row => row.errors.length === 0 && row.values.productType === '200000000')
        .forEach(row => {
          const result = settingsByProduct.get(row.values.itemNumber)
          if (!result?.success || !result.data?.hasSettings) {
            row.errors.push({
              field: 'itemNumber',
              message: t('quotation:lines.quantitySettingsUnavailable')
            })
            return
          }

          row.values.orderQuantitySettings = result.data
          const validation = validateOrderQuantity(row.values.requestedQty, result.data)
          if (!validation.isValid) {
            const errorKey = getOrderQuantityErrorKey(validation)
            row.errors.push({
              field: 'requestedQty',
              message: t(errorKey || 'quotation:lines.quantityInvalid', validation)
            })
          }
        })

      const invalidRows = parsedRows.filter(row => row.errors.length > 0)
      const validRows = parsedRows.filter(row => row.errors.length === 0)

      if (validRows.length > 0) {
        const existingLines = isEditMode ? editedLines : lines
        const maxOrder = Math.max(0, ...existingLines.map(line => parseInt(line.mserp_alg_reportdisplayorder, 10) || 0))
        const importedLines = validRows.map((row, index) => buildImportedLine(row, index, maxOrder + ((index + 1) * 10)))
        const startIndex = editedLines.length
        setEditedLines(previous => [...previous, ...importedLines])
        setModifiedFields(previous => {
          const next = new Set(previous)
          importedLines.forEach((line, index) => {
            ;['mserp_itemnumber', 'mserp_requestedsalesquantity', 'mserp_alg_producttype', 'mserp_salesprice', 'mserp_linediscountpercentage', 'mserp_alg_netsalesprice', 'mserp_lineamount'].forEach(field => next.add(`${startIndex + index}-${field}`))
          })
          return next
        })
        setIsEditMode(true)
      }

      setImportErrors(invalidRows)
      setImportSummary({ fileName: file.name, imported: validRows.length, rejected: invalidRows.length })
    } catch (importError) {
      console.error('Error importing quotation lines:', importError)
      setImportErrors([{ rowNumber: 1, errors: [{ field: 'file', message: importError.message || t('quotation:lines.import.errors.invalidFile') }] }])
    }
  }

  return {
    importErrors,
    importSummary,
    importInputRef,
    handleImportFile,
    downloadImportTemplate,
    getImportColumnLabel
  }
}
